import { useEffect } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import { useDebounce } from "hooks/useDebounce";
import { FilledInputProps, IconButton, InputAdornment, TextField, Tooltip } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import PlaceIcon from "@mui/icons-material/Place";

type MapSearchFieldProps = {
  value: string;
  onChange: (query: string) => void;
  onResults?: (results: google.maps.places.Place[]) => void;
};

function centerMapOnPlaces(map: google.maps.Map, places: google.maps.places.Place[]) {
  const bounds = new google.maps.LatLngBounds();
  places.forEach((place) => {
    if (place.location) bounds.extend(place.location);
  });

  // Prevent the map from zooming in too much when there are only a few results
  const listener = map.addListener("bounds_changed", () => {
    const TARGET_MAX_ZOOM = 15;
    if ((map.getZoom() || 0) > TARGET_MAX_ZOOM) {
      map.setZoom(TARGET_MAX_ZOOM);
    }
    google.maps.event.removeListener(listener);
  });

  map.fitBounds(bounds);
}

const MapSearchField: React.FC<MapSearchFieldProps> = (props) => {
  const { value, onChange, onResults } = props;

  const map = useMap();

  const debouncedSearch = useDebounce(value, { delayMs: 500 });

  useEffect(() => {
    if (!debouncedSearch) {
      onResults?.([]);
      return;
    }

    google.maps.places.Place.searchByText({
      textQuery: debouncedSearch,
      fields: ["displayName", "location", "addressComponents"],
      region: "SE",
      locationBias: map?.getCenter(),
    }).then(({ places }) => {
      onResults?.(places);
      centerMapOnPlaces(map!, places);
    });
  }, [map, onResults, debouncedSearch]);

  return (
    <TextField
      label="Sök adress"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
      variant="filled"
      slotProps={{
        input: {
          disableUnderline: true,
          startAdornment: (
            <InputAdornment position="start">
              <PlaceIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title="Rensa sökning" arrow>
                <IconButton onClick={() => onChange("")} hidden={value === ""}>
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        } as Partial<FilledInputProps>,
      }}
      sx={(theme) => ({
        maxWidth: 350,
        "& .MuiFilledInput-root": {
          overflow: "hidden",
          borderRadius: 1,
          border: "none",
          backgroundColor: "#ffffff",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2), 0 1px 0 1px rgba(0, 0, 0, 0.1)",
          transition: theme.transitions.create(["border-color", "background-color", "box-shadow"]),
          "&:hover": {
            backgroundColor: "#f8f8f8",
          },
          "&.Mui-focused": {
            backgroundColor: "#ffffff",
            boxShadow: "0 2px 12px rgba(0, 0, 0, 0.2)",
            outline: "1px solid",
            outlineColor: theme.palette.primary.main,
          },
        },
      })}
    />
  );
};

export default MapSearchField;
